import React, {
  memo, useCallback, useEffect, useMemo, useRef, useState,
} from 'react'

import {
  Button, Checkbox, Icon, ThumbUp, User,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, object } from 'prop-types'

import FilterSearchInput from 'Pods/SearchPage/Components/Shared/FilterSearchInput/FilterSearchInput'
import useTranslate from 'Pods/SearchPage/Shared/useTranslate'
import {
  addDesigner, clearDesigners, removeDesigner, setDesigners, setDesignerScopes, setDesignerSelection,
} from 'Pods/SearchPage/Store/actions/designers'
import { useSearchPageStore } from 'Pods/SearchPage/Store/index'

import { EMPTY_ARRAY } from 'Utils/stableDefaults'

import {
  DESIGNER_SCOPES, DESIGNERS_PAGE_SIZE, SCOPE_PARAMS, SCOPE_PREDICATES, scopeMembers,
} from './constants'
import useDesignerLookup, { normalizeTerm } from './useDesignerLookup'
import useDesignerOptions from './useDesignerOptions'

// The endpoint returns the top designers plus everyone the viewer follows, so a selection carried
// in from a shared URL (?author[]=...) can be missing from it. Surface those as label-only rows so
// the filter stays visible and uncheckable instead of applying invisibly. A designer picked from a
// lookup keeps the row the lookup returned, stats included.
const withUnlistedSelections = (options, selected, known) => {
  const listed = new Set(options.map(option => option.username))
  const unlisted = selected.filter(username => !listed.has(username))

  if (unlisted.length === 0) {
    return options
  }

  return [
    ...unlisted.map(username => known.find(designer => designer.username === username) || {
      username,
      modelCount: null,
      ratingPercent: null,
      topDesigner: false,
      following: false,
    }),
    ...options,
  ]
}

function DesignerContent({ hideReset = false, searchRef = null }) {
  const translate = useTranslate()

  // Two slices back this filter. `designers` holds the usernames going out as `author[]`;
  // `designerScopes` holds the scopes with a backend flag (SCOPE_PARAMS) ticked whole, so a
  // 74-designer selection is one param rather than 74. A scope without a flag, or one the user has
  // partly unticked, lives in `designers` as its members.
  const designers = useSearchPageStore(state => state.designers ?? EMPTY_ARRAY)
  const scopes = useSearchPageStore(state => state.designerScopes ?? EMPTY_ARRAY)
  const { designers: options, isLoading } = useDesignerOptions()
  const [query, setQuery] = useState('')
  const { designers: lookupHits, isLoading: isLookingUp } = useDesignerLookup(query)
  // Lookup rows the user ticked, so they keep their stats once the query moves on and the lookup
  // no longer returns them.
  const [pickedHits, setPickedHits] = useState(EMPTY_ARRAY)
  const [visibleCount, setVisibleCount] = useState(DESIGNERS_PAGE_SIZE)
  // A scope stays in view after a member is unticked, so the user can carry on deselecting instead
  // of being thrown back to the full list. It leaves the checked state at that point but not this.
  const [openScopes, setOpenScopes] = useState(() => scopes)
  // The scopes the user broke open here. Only they may stay in view while unchecked: a scope that
  // went off any other way (drawer footer Reset, Clear all, a popstate restore) has to leave with it.
  const brokenOpenScopes = useRef(new Set())

  const membersByScope = useMemo(() => Object.fromEntries(
    DESIGNER_SCOPES.map(scope => [scope, scopeMembers(options, scope)]),
  ), [options])

  const isSelected = useCallback((designer) => (
    designers.includes(designer.username) || scopes.some(scope => SCOPE_PREDICATES[scope](designer))
  ), [designers, scopes])

  // A flagged scope is on while its flag is; an expanded one while every member sits in `author[]`,
  // so unticking a single member takes the chip off with it.
  const isScopeChecked = useCallback((scope) => {
    if (SCOPE_PARAMS[scope]) {
      return scopes.includes(scope)
    }
    const members = membersByScope[scope]

    return members.length > 0 && members.every(username => designers.includes(username))
  }, [designers, membersByScope, scopes])

  // Reconcile the narrowing with the store on every external write: an unchecked scope keeps the
  // list narrowed only while it is one the user broke open, and once nothing is selected at all the
  // narrowing has nothing left to serve - keeping it would drop the next individual pick into a
  // list still cut down to a scope the user already dropped.
  useEffect(() => {
    if (designers.length === 0 && scopes.length === 0) {
      brokenOpenScopes.current.clear()
    }
    setOpenScopes((previous) => {
      const next = previous.filter(scope => isScopeChecked(scope) || brokenOpenScopes.current.has(scope))

      return next.length === previous.length ? previous : next
    })
  }, [designers, isScopeChecked, scopes])

  const selectedCount = useMemo(() => {
    const covered = new Set(designers)
    scopes.forEach(scope => membersByScope[scope].forEach(username => covered.add(username)))

    return covered.size
  }, [designers, membersByScope, scopes])

  const toggleScope = useCallback((scope) => {
    const members = membersByScope[scope]
    brokenOpenScopes.current.delete(scope)

    if (isScopeChecked(scope)) {
      setOpenScopes(previous => previous.filter(open => open !== scope))
      if (SCOPE_PARAMS[scope]) {
        setDesignerScopes(scopes.filter(active => active !== scope))
      } else {
        setDesigners(designers.filter(username => !members.includes(username)))
      }
      return
    }

    setOpenScopes(previous => (previous.includes(scope) ? previous : [...previous, scope]))
    if (SCOPE_PARAMS[scope]) {
      // Members already spelled out in `author[]` stay there: the backend unions the two, and leaving
      // them alone means nothing has to be remembered to put them back when the flag goes away.
      setDesignerScopes([...scopes, scope])
    } else {
      setDesigners([...new Set([...designers, ...members])])
    }
  }, [designers, isScopeChecked, membersByScope, scopes])

  const handleChange = useCallback((username, isChecked) => {
    if (isChecked) {
      const hit = lookupHits.find(designer => designer.username === username)
      if (hit) {
        setPickedHits(previous => (
          previous.some(designer => designer.username === username) ? previous : [...previous, hit]
        ))
      }
      addDesigner(username)
      return
    }

    // Every checked scope this designer belongs to goes unchecked with them, and stays in view so the
    // user can carry on deselecting. The row may come from a lookup rather than the options: a
    // followed designer past the options' 100-follow cap renders checked through the Following flag,
    // and unticking them has to break that flag open too or the tick would never come off.
    const designer = [...options, ...lookupHits, ...pickedHits].find(option => option.username === username) || {}
    const covering = DESIGNER_SCOPES.filter(scope => isScopeChecked(scope) && SCOPE_PREDICATES[scope](designer))
    covering.forEach(scope => brokenOpenScopes.current.add(scope))

    // Unticking someone a whole-scope flag covers has to break that flag open: the scope drops to
    // its remaining members as individual `author[]` entries so the rest stay selected.
    const flagged = covering.filter(scope => scopes.includes(scope))

    if (flagged.length > 0) {
      const freed = flagged.flatMap(scope => membersByScope[scope])
      setDesignerSelection(
        [...new Set([...designers, ...freed])].filter(name => name !== username),
        scopes.filter(scope => !flagged.includes(scope)),
      )
      return
    }

    removeDesigner(username)
  }, [designers, isScopeChecked, lookupHits, membersByScope, options, pickedHits, scopes])

  const handleReset = useCallback(() => {
    clearDesigners()
  }, [])

  // A new query is a new list, so the cut starts over; otherwise a "Show more" pressed on the full
  // list would carry a 40-row cut into a two-row search result and back.
  const handleQueryChange = useCallback((value) => {
    setQuery(value)
    setVisibleCount(DESIGNERS_PAGE_SIZE)
  }, [])

  const handleShowMore = useCallback(() => {
    setVisibleCount(previous => previous + DESIGNERS_PAGE_SIZE)
  }, [])

  const term = normalizeTerm(query)

  const visibleDesigners = useMemo(() => {
    const all = withUnlistedSelections(options, designers, pickedHits)

    // Typing searches the whole catalog: someone typing a name wants that designer, so the scope
    // narrowing steps aside (the chip stays checked) and the lookup hits follow the local matches.
    if (term) {
      const listed = new Set(all.map(designer => designer.username))

      return [
        ...all.filter(designer => designer.username.toLowerCase().includes(term)),
        ...lookupHits.filter(designer => !listed.has(designer.username)),
      ]
    }

    // Scopes within the group are any-of, matching how the array-valued filters behave. A designer
    // picked outside the open scopes (typically from a lookup) stays listed, or there would be no
    // way to untick them short of searching for them again.
    const predicates = openScopes.map(scope => SCOPE_PREDICATES[scope])

    return all.filter(designer => (
      predicates.length === 0
        || designers.includes(designer.username)
        || predicates.some(predicate => predicate(designer))
    ))
  }, [designers, lookupHits, openScopes, options, pickedHits, term])

  const shownDesigners = useMemo(
    () => visibleDesigners.slice(0, visibleCount),
    [visibleDesigners, visibleCount],
  )
  const hasMore = visibleDesigners.length > shownDesigners.length

  const statusKey = useMemo(() => {
    if (isLoading) {
      return 'loading'
    }
    if (term) {
      return isLookingUp ? 'searching' : 'no_results'
    }
    // A viewer who follows nobody gets told why the list is empty rather than the generic miss.
    if (openScopes.includes('following') && membersByScope.following.length === 0) {
      return 'no_following'
    }

    return 'no_results'
  }, [isLoading, isLookingUp, membersByScope, openScopes, term])

  return (
    <div className="designer-content">
      <FilterSearchInput
        id="designer-search"
        value={query}
        onChange={handleQueryChange}
        placeholder={translate('search_page.filters.designers.search_placeholder')}
        ariaLabel={translate('search_page.filters.designers.title')}
        inputRef={searchRef}
      />

      <div className="designer-content__scopes">
        {DESIGNER_SCOPES.map((scope) => {
          const isChecked = isScopeChecked(scope)

          return (
            <Checkbox
              key={scope}
              id={`designer-scope-${scope}`}
              name={`designer-scope-${scope}`}
              size="small"
              isControlled
              isChecked={isChecked}
              // An empty scope would apply nothing (`following=1` with no follows sends an empty
              // user_ids array, which the backend drops) while claiming to filter. A hydrated one
              // stays enabled so the user can still take it off.
              isDisabled={!isChecked && membersByScope[scope].length === 0}
              onChange={() => toggleScope(scope)}
              className="designer-content__scope"
              wrapperClassName="designer-content__scope-wrapper"
              labelClassName="designer-content__scope-label cgt-caption-semibold"
              label={translate(`search_page.filters.designers.${scope}`)}
            />
          )
        })}
      </div>

      {(isLoading || visibleDesigners.length === 0) && (
        <p className="designer-content__status cgt-body-base" aria-live="polite">
          {translate(`search_page.filters.designers.${statusKey}`)}
        </p>
      )}

      <div className="designer-content__list">
        {shownDesigners.map(designer => (
          <Checkbox
            key={designer.username}
            id={`designer-${designer.username}`}
            name={`designer-${designer.username}`}
            isControlled
            isChecked={isSelected(designer)}
            onChange={isChecked => handleChange(designer.username, isChecked)}
            // Username lives in the checkbox label so the input has an accessible name; the
            // stats sit on a second line below it.
            label={(
              <span className="designer-content__info">
                <span className="cgt-body-base-semibold">{designer.username}</span>
                <span className="designer-content__meta cgt-caption">
                  {designer.modelCount !== null && (
                    <span className="designer-content__models cgt-caption-semibold">
                      {translate('search_page.filters.designers.models_count', { count: designer.modelCount })}
                    </span>
                  )}
                  {designer.ratingPercent !== null && (
                    <span className="designer-content__rating cgt-caption-semibold">
                      <Icon
                        notSpaced
                        svgComponent={ThumbUp}
                        size={{ width: '10', height: '10' }}
                        color="dark"
                        className="designer-content__rating-icon"
                      />
                      {`${designer.ratingPercent}%`}
                    </span>
                  )}
                </span>
              </span>
            )}
            trailLabel={designer.following ? (
              <Icon
                notSpaced
                svgComponent={User}
                size="tiny"
                color="dark"
                className="designer-content__following-icon"
                aria-label={translate('search_page.filters.designers.following_indicator')}
              />
            ) : undefined}
          />
        ))}
      </div>

      {hasMore && (
        <Button
          type="ghost-primary"
          size="small"
          actionType="button"
          customClass="filter-wrapper__show-more cgt-link-primary"
          onClick={handleShowMore}
        >
          {translate('search_page.filters.designers.show_more')}
        </Button>
      )}

      {!hideReset && (
        <Button
          type="ghost"
          size="tiny"
          customClass="filter-wrapper__reset-button"
          onClick={handleReset}
          // Off the slices, not the count: a hydrated `following=1` for a viewer who follows nobody
          // is an active filter with zero resolvable members, and Reset has to be able to clear it.
          disabled={designers.length === 0 && scopes.length === 0}
        >
          {selectedCount > 0
            ? `${translate('search_page.filters.more_filters.reset')} (${selectedCount})`
            : translate('search_page.filters.more_filters.reset')}
        </Button>
      )}
    </div>
  )
}

DesignerContent.propTypes = {
  hideReset: bool,
  searchRef: object,
}

export default memo(DesignerContent)
